import React from 'react';
import { 
  Typography, 
  Paper, 
  Button, 
  Collapse,
  IconButton
} from '@mui/material';
import { 
  Error as ErrorIcon, 
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon
} from '@mui/icons-material';
import { makeStyles } from '@mui/styles';

const useStyles = makeStyles((theme) => ({
  errorContainer: {
    padding: theme.spacing(3),
    margin: theme.spacing(2),
    backgroundColor: theme.palette.background.paper,
    borderRadius: theme.shape.borderRadius,
    border: `1px solid ${theme.palette.error.main}`,
  },
  errorHeader: {
    display: 'flex',
    alignItems: 'center',
    marginBottom: theme.spacing(2),
    color: theme.palette.error.main,
  },
  errorIcon: {
    marginRight: theme.spacing(1),
    fontSize: '2rem',
  },
  errorTitle: {
    fontWeight: 'bold',
    marginBottom: theme.spacing(1),
  },
  errorMessage: {
    marginBottom: theme.spacing(2),
    fontFamily: 'monospace',
    backgroundColor: theme.palette.grey[100],
    padding: theme.spacing(1),
    borderRadius: theme.shape.borderRadius,
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-word',
    maxHeight: '300px',
    overflow: 'auto',
  },
  errorStack: {
    marginTop: theme.spacing(2),
  },
  actionButtons: {
    marginTop: theme.spacing(2),
    display: 'flex',
    gap: theme.spacing(1),
  },
  expandButton: {
    marginLeft: 'auto',
  },
}));

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
      showDetails: false,
    };
  }

  static getDerivedStateFromError(error) {
    // Update state so the next render will show the fallback UI
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    // Log the error to console for developers
    console.error('ErrorBoundary caught an error:', error, errorInfo);
    
    // Update state with error details
    this.setState({
      error: error,
      errorInfo: errorInfo,
    });

    // You could also log the error to an error reporting service here
    // logErrorToService(error, errorInfo);
  }

  handleRefresh = () => {
    window.location.reload();
  };

  handleReset = () => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
      showDetails: false,
    });
  };

  toggleDetails = () => {
    this.setState(prevState => ({
      showDetails: !prevState.showDetails,
    }));
  };

  render() {
    if (this.state.hasError) {
      return (
        <div className={this.props.classes.errorContainer}>
          <div className={this.props.classes.errorHeader}>
            <ErrorIcon className={this.props.classes.errorIcon} />
            <Typography variant="h5" component="h1">
              Something went wrong
            </Typography>
            <IconButton
              className={this.props.classes.expandButton}
              onClick={this.toggleDetails}
              size="small"
            >
              {this.state.showDetails ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            </IconButton>
          </div>

          <Typography variant="body1" color="textSecondary" paragraph>
            We're sorry, but something unexpected happened. The error has been logged and our team has been notified.
          </Typography>

          <Collapse in={this.state.showDetails}>
            <Paper 
              elevation={1} 
              className={this.props.classes.errorStack}
              style={{ 
                backgroundColor: '#ffebee', 
                border: '1px solid #f44336',
                padding: '16px'
              }}
            >
              <Typography variant="h6" component="div" style={{ color: '#d32f2f', marginBottom: '8px' }}>
                Error Details
              </Typography>
              <Typography variant="body2" component="div">
                <strong>Error:</strong> {this.state.error?.toString()}
              </Typography>
              
              {this.state.errorInfo?.componentStack && (
                <div style={{ marginTop: '8px' }}>
                  <Typography variant="body2" component="div">
                    <strong>Component Stack:</strong>
                  </Typography>
                  <pre className={this.props.classes.errorMessage}>
                    {this.state.errorInfo.componentStack}
                  </pre>
                </div>
              )}
            </Paper>
          </Collapse>

          <div className={this.props.classes.actionButtons}>
            <Button
              variant="contained"
              color="primary"
              onClick={this.handleRefresh}
            >
              Refresh Page
            </Button>
            <Button
              variant="outlined"
              color="primary"
              onClick={this.handleReset}
            >
              Try Again
            </Button>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

// Wrapper component to use hooks
const ErrorBoundaryWrapper = (props) => {
  const classes = useStyles();
  return <ErrorBoundary {...props} classes={classes} />;
};

export default ErrorBoundaryWrapper;
